import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, BookOpen, Star, Shield } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

const PACKAGES = [
  { id: 'starter', name: 'Starter', price: '$149/mo', features: ['15,000 words/month', '2 revisions', 'Digital delivery'] },
  { id: 'professional', name: 'Professional', price: '$296/mo', features: ['50,000 words/month', '5 revisions', 'Cover consultation'], popular: true },
  { id: 'complete', name: 'Complete', price: '$599/mo', features: ['Unlimited words', 'Unlimited revisions', 'Cover + Marketing'] },
];

export default function AffiliatePromo() {
  const { code } = useParams();
  const [affiliate, setAffiliate] = useState(null);

  useEffect(() => {
    if (code) {
      api.get(`/affiliate/public/${code}`).then(r => setAffiliate(r.data)).catch(() => {});
      api.post('/affiliate/track-click', { affiliate_code: code, page: 'promo' }).catch(() => {});
    }
  }, [code]);

  return (
    <>
      <SeoHead title={`Special Offer — AI Ghostwriter Pro`} description="Get your book written by professional AI ghostwriters. Special partner offer with plans from $149/mo." />
      <section className="pt-28 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          {affiliate && (
            <div className="text-center mb-6">
              <p className="text-xs font-sans uppercase tracking-[0.2em] text-primary font-bold">Recommended by {affiliate.name}</p>
            </div>
          )}
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4" data-testid="promo-title">
              Turn Your Book Idea Into <span className="text-primary">Reality</span>
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">Professional AI ghostwriting from $149/mo. 7-day free trial — no credit card required.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {PACKAGES.map(pkg => (
              <div key={pkg.id} className={`bg-white/5 border rounded-sm p-6 relative ${pkg.popular ? 'border-primary/50 shadow-[0_0_30px_-10px_rgba(212,175,55,0.3)]' : 'border-white/10'}`} data-testid={`promo-pkg-${pkg.id}`}>
                {pkg.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm">Most Popular</div>}
                <h3 className="font-serif text-lg mb-1">{pkg.name}</h3>
                <p className="text-2xl font-serif text-primary mb-4">{pkg.price}</p>
                <ul className="space-y-2 mb-6">
                  {pkg.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle className="w-3 h-3 text-green-400 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={`/get-started${code ? `?ref=${code}` : ''}`} className="block w-full text-center rounded-sm py-3 bg-primary text-primary-foreground font-semibold text-sm hover:bg-white hover:text-black transition-all">
                  Start Free Trial
                </Link>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {[
              { icon: BookOpen, title: 'Expert Writing', desc: 'AI-powered ghostwriting delivering publication-ready manuscripts' },
              { icon: Star, title: 'Get Reviews', desc: 'Join our free Authors\' Review Exchange for Amazon reviews' },
              { icon: Shield, title: 'Full Ownership', desc: 'You own 100% of the copyright. Your name, your book.' },
            ].map((item, i) => (
              <div key={i} className="p-6">
                <item.icon className="w-6 h-6 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="font-serif text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
